const selectors = {
  componentsCollectionJSON: '[data-components-collection-json]',
  atcButtonLabel: '.fc-add-to-cart-button .fc-button-label',
  warning: '[data-product-customizer-warning]',
};

class ProductCustomizer {
  /**
   * ProductCustomizer constructor (#fluidConfigure element wrapper)
   * @param {jQuery | HTMLElement} el - Main element
   */
  constructor(el) {
    this.settings = {};
    this.name = 'productCustomizer';

    this.$el = $(el);

    // Bail if no element provided.
    if (!this.$el || this.$el === undefined) {
      console.warn(`[${this.name}] - $el required to initialize`);
      return;
    }

    this.customProductURL = this.$el.data('custom-product-url');
    this.customProductVariantID = this.$el.data('custom-product-variant-id');
    this.cartPageUrl = (this.$el.attr('data-cart-url') || '').trim();
    this.$warning = $(selectors.warning, this.$el);
    this.componentsCollectionHandle = this.$el.attr('data-custom-product-components');

    // Create property to store reference to component variants
    // Will be replaced with an array of objects if the call succeeds.
    this.componentVariants = [];

    // Use component collection handle to fetch product
    this._fetchComponentProducts(this.componentsCollectionHandle)
      .then((variants) => {
        this.componentVariants = variants;
      })
      .catch((e) => {
        // If something goes wrong, set componentVariants to null.
        // We'll use the non-array value to determine if the fetch failed
        console.warn(`Unable to fetch components collection with handle: ${this.componentsCollectionHandle}`);
        this.componentVariants = null;
      });

    this._initStickyHeaderHeight();

    // If user came from cart link (?recipe=xxx), fetch cart and set URL to edit mode (?editing=key1,key2)
    // so that when they click Add to cart we use the existing edit flow (replace instead of add).
    this._maybeSetEditModeFromRecipeUrl();
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this._maybeSetEditModeFromRecipeUrl());
    }
    window.addEventListener('load', () => this._maybeSetEditModeFromRecipeUrl());
    setTimeout(() => this._maybeSetEditModeFromRecipeUrl(), 1200);

    /* Function calls
     * ================================================================== */

    // The configureID functions need to be defined in the window scope.
    // If an uuid search param is seen in URL, configureEditCart will run.
    // The uuid can be anything at the moment. It is the orderItemId passed in this function.
    window.configureEditCart = (recipeId, orderItemId) => {
      this._addBundleToCart(recipeId, true);
    };

    window.configureAddToCart = (recipeId) => {
      this._addBundleToCart(recipeId, false);
    };
  }

  /**
   * Measures utility bar + sticky header and sets --sticky-header-height.
   * Recalculates on viewport resize so .custom-watch-wrapper stays viewport-tall.
   */
  _initStickyHeaderHeight() {
    this._setStickyHeaderHeight();
    // Remeasure after fonts/layout settle — header height can shift slightly.
    window.addEventListener('load', () => this._setStickyHeaderHeight(), { once: true });

    let resizeTimeout;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => this._setStickyHeaderHeight(), 100);
    });
  }

  _setStickyHeaderHeight() {
    const stickyHeader =
      document.querySelector('sticky-header') || document.querySelector('.header-wrapper');
    if (!stickyHeader) return;

    const announcementSection =
      document.querySelector('.announcement-bar-section') ||
      document.querySelector('.utility-bar')?.closest('.shopify-section');
    const headerSection = stickyHeader.closest('.shopify-section') || stickyHeader;

    let height = 0;

    // Prefer the geometric span of the full header stack (includes section
    // chrome / borders missed when summing inner .utility-bar + sticky-header).
    // Fall back to summing section boxes when the announcement bar is fixed
    // (out of flow) and no longer sits above the header in layout.
    if (announcementSection && announcementSection !== headerSection) {
      const announcementStyles = window.getComputedStyle(announcementSection);
      const announcementOutOfFlow =
        announcementStyles.position === 'fixed' || announcementStyles.position === 'absolute';

      if (announcementOutOfFlow) {
        height =
          announcementSection.getBoundingClientRect().height +
          headerSection.getBoundingClientRect().height;
      } else {
        height =
          stickyHeader.getBoundingClientRect().bottom -
          announcementSection.getBoundingClientRect().top;
      }
    } else {
      height = headerSection.getBoundingClientRect().height;
    }

    if (height > 0) {
      // Ceil so calc(100dvh - var) never leaves a gap under the header.
      document.documentElement.style.setProperty(
        '--sticky-header-height',
        `${Math.ceil(height)}px`
      );
    }
  }

  /**
   * Fetches the collection containining this custom product's
   * components.  Necessary for the add-to-cart method to function.
   * @param {String} collectionHandle
   * @returns {Promise} resolves with the array of component products present in the component collection
   */

  getCollectionByHandle(handle) {
    return fetch(`/collections/` + handle + `?view=custom-feed-json`).then((response) => response.json());
  }

  async _fetchComponentProducts(collectionHandle) {
    let componentsCollection = null;
    try {
      componentsCollection = await this.getCollectionByHandle(collectionHandle);
    } catch (e) {
      throw new Error(e);
    }

    return componentsCollection?.products;
  }

  /**
   * Method to add an item to cart.  Is exposed to, and called by, the
   * Fluid Configure app
   * @param {Object} recipeId - an object from Fluid Configure containing details on the custom built product
   * @param {Boolean} editMode
   * @returns {Null}
   */
  _addBundleToCart(recipeId, editMode) {
    if (typeof recipeId === 'undefined') return;

    // We only need to look for these component 'types' to pair with Shopify products
    const componentKeysWithProducts = ['band', 'case', 'buckle', 'face', 'second'];

    // Destructure recipe data
    const componentsData = recipeId.extended_attributes;
    const componentSelections = { ...recipeId.custom.selections };
    const image = `${recipeId.images.view_1}&width=300&height=300`;
    const url = `${this.customProductURL}?recipe=${recipeId.id}`;
    const timeStamp = recipeId.creation_date;
    //const price = recipeId.prices[window.Shopify.currency.active];
    // const fontSize = componentsData.font_size.value.info.description;
    // Dont need this mapping as font_size value is available in main selections attribute
    // NIX-778 mapping directly to custom json attributes

    // Original font_size value 'Aa' should be swapped to 'Small/Big'
    // componentSelections.font_size = fontSize;
    // Don't need this mapping as font_size value is available in main selections attribute
    // NIX-778 mapping directly to custom json attributes

    // Change custom engraving property from personalization_1line to personalization_line1
    if (componentSelections.hasOwnProperty('personalization_1line')) {
      componentSelections.personalization_line1 = componentSelections.personalization_1line;
      delete componentSelections.personalization_1line;
    }

    // `extended_attributes` contains a *lot* of data. We only care about the
    // k/v's that need to be mapped to Shopify products (see arr above).
    // Reduce out an object where key = component type, and value = SKU
    const componentSkusInBundle = Object.keys(componentsData).reduce((obj, key) => {
      if (componentKeysWithProducts.includes(key)) {
        const data = componentsData[key];
        const sku = data?.value?.info?.vendorId || null;
        if (sku) {
          return { ...obj, [key]: sku };
        }
      }
      return obj;
    }, {});

    // Cross reference the list of component skus with the variants list
    // fetched from this custom product's components collection.
    const variantsInBundle = this.componentVariants.filter((variant) =>
      Object.values(componentSkusInBundle).includes(variant.sku)
    );

    // Make sure all variants are accounted for before continuing.
    const allVariantsAvailable =
      variantsInBundle.length === Object.values(componentSkusInBundle).length &&
      variantsInBundle.every((v) => v.available);

    // Recipe id as non-private so cart API returns it (private _recipe_id is often omitted in /cart.js response).
    const recipeIdVal = String(recipeId.id);
    const items = [
      // Component Variants
      ...variantsInBundle.map((v) => ({
        quantity: 1,
        id: v.id,
        properties: {
          _time_stamp: timeStamp,
          _recipe_id: recipeId.id,
          _parts_recipe_id: recipeId.id,
          recipe_id: recipeIdVal,
        },
      })),
      // Parent Product
      {
        quantity: 1,
        id: this.customProductVariantID,
        properties: {
          _time_stamp: timeStamp,
          _recipe_id: recipeId.id,
          _image: image,
          _url: url,
          _resource_url: recipeId.uri,
          recipe_id: recipeIdVal,
          //_price: price,
          ...componentSelections,
          ...Object.keys(componentSkusInBundle).reduce(
            (obj, key) => ({
              ...obj,
              [`_${key}_SKU`]: componentSkusInBundle[key],
            }),
            {}
          ),
        },
      },
    ];

    // If we're missing variants, error
    // Note, we can be missing items because (a) they're out of stock,
    // or (b) the SKU isn't present in the components array.  Either way,
    // we show the same error.
    if (!allVariantsAvailable) {
      // Filter for the unavailable items
      const unavailableItems = Object.keys(componentSkusInBundle).filter((k) => {
        const sku = componentSkusInBundle[k];
        return !variantsInBundle.some((v) => v.sku === sku && v.available);
      });

      // Show warning detailing the unavailable component types
      // TODO - This is way too subtle and needs to be more prominent.
      // Leaving as is for now due to time constraints
      // this.$warning.text(
      // `${window.theme.strings.unavailable}: ${unavailableItems}`
      // );
      this.updateCtaState('unavailable');
      setTimeout(() => {
        this.updateCtaState(editMode ? 'edit' : 'add');
      }, 3000);

      return;
    }

    // Edit mode: from configureEditCart (editing= in URL) or from cart link (we set editing= on load).
    // Same flow: remove existing bundle by keys, add new bundle, open side cart.
    const itemsBeingEdited = this.getItemsBeingEdited();
    const isEdit = editMode || (itemsBeingEdited && itemsBeingEdited.length > 0);

    if (isEdit) {
      this.updateCtaState(editMode ? 'editing' : 'adding');
      const qtyByKey = itemsBeingEdited
        ? itemsBeingEdited.reduce((obj, key) => ({ ...obj, [key]: 0 }), {})
        : {};

      const runEdit = () => {
        this.updateLineItemQuantitiesByKey(qtyByKey)
          .then(() => this.addItemsToCart(items))
          .done((response) => {
            this.updateCtaState(editMode ? 'edit' : 'add');
            this.openSideCart(response);
          })
          .fail(() => {
            console.warn('something went wrong...');
            this.updateCtaState(editMode ? 'edit' : 'add');
          });
      };

      if (this.cart) {
        runEdit();
      } else {
        fetch('/cart.js')
          .then((res) => res.json())
          .then((cart) => {
            this.cart = cart;
            runEdit();
          })
          .catch(() => runEdit());
      }
      return;
    }

    // The configureAddToCart function runs when editMode is false (by default).
    // A watch and the watch components are added to cart as a bundle.
    this.updateCtaState('adding');
    this.addItemsToCart(items)
      .done((response) => {
        this.updateCtaState('add');
        this.openSideCart(response);
      })
      .fail(() => {
        this.updateCtaState('add');
      });
  }

  /**
   * Updates the CTA label in response to add/edit events
   * @param {String} state ['add', 'adding', 'edit', 'editing', 'unavailable']
   */
  updateCtaState(state) {
    const S = window.productCustomizerStrings || {};
    let newLabel = '';
    switch (state) {
      case 'adding':
        newLabel = S.adding || 'Adding...';
        break;
      case 'edit':
        newLabel = S.updateCart || 'Update cart';
        break;
      case 'editing':
        newLabel = S.updating || 'Updating...';
        break;
      case 'unavailable':
        newLabel = S.unavailable || 'Unavailable';
        break;
      default:
        newLabel = S.addToCart || 'Add to cart';
    }

    this.$el.find(selectors.atcButtonLabel).html(newLabel);
  }

  addItemsToCart(items, emitCartEvents) {
    const promise = $.Deferred();
    const cartDrawer = document.querySelector('cart-drawer');
    const data = { items };

    // Request drawer + bubble HTML so we can open the side cart (same as product-form.js)
    if (cartDrawer && typeof cartDrawer.getSectionsToRender === 'function') {
      data.sections = cartDrawer
        .getSectionsToRender()
        .map((section) => section.id)
        .join(',');
      data.sections_url = window.location.pathname;
    }

    $.ajax({
      type: 'POST',
      dataType: 'json',
      contentType: 'application/json',
      url: (window.routes && window.routes.cart_add_url) || '/cart/add.js',
      data: JSON.stringify(data),
      success: (response) => {
        promise.resolve(response);
      },
      error: (response) => {
        promise.reject({
          message:
            window.productCustomizerStrings?.quantityNotAvailable ||
            'The quantity you entered is not available.',
        });
        if (emitCartEvents) {
          const event = $.Event('addFail.ajaxFormManager', { data: response });
          $window.trigger(event);
        }
      },
    });

    return promise;
  }

  /**
   * Refreshes and opens the side cart when available; otherwise falls back to cart page.
   * @param {Object} addResponse - /cart/add.js response (optionally with sections)
   */
  openSideCart(addResponse) {
    const cartDrawer = document.querySelector('cart-drawer');

    if (cartDrawer && addResponse?.sections && typeof cartDrawer.renderContents === 'function') {
      cartDrawer.classList.remove('is-empty');
      if (typeof cartDrawer.setActiveElement === 'function') {
        cartDrawer.setActiveElement(document.activeElement);
      }
      cartDrawer.renderContents(addResponse);

      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'product-customizer' });
      }
      return;
    }

    this.redirectToCart();
  }

  /**
   * Updates multiple quantities of items in the cart by line-item key.
   * @param {Object} newQuantities - an object of k/v, where key = line-item key (string), and value = quantity.
   */
  updateLineItemQuantitiesByKey(newQuantities) {
    const promise = $.Deferred();

    if (!this.cart || typeof newQuantities !== 'object') {
      return promise.reject();
    }

    // Shopify accepts updates as object keyed by line item key (string) for precise targeting
    const updates = {};
    this.cart.items.forEach((item) => {
      const key = item.key;
      updates[key] = key in newQuantities ? newQuantities[key] : item.quantity;
    });

    $.ajax({
      type: 'post',
      dataType: 'json',
      url: '/cart/update.js',
      contentType: 'application/json',
      data: JSON.stringify({ updates }),
      success: (cart) => {
        promise.resolve(cart);
      },
      error: () => {
        const data = {
          message: window.productCustomizerStrings?.genericError || 'Something went wrong.',
        };
        promise.reject(data);
      },
    });

    return promise;
  }

  /**
   * Redirects to the cart page
   */
  redirectToCart() {
    window.location.href = this.cartPageUrl || '/cart';
  }

  parseQueryString(url) {
    const queryString = url.split('?')[1];
    if (!queryString) return {};

    const params = {};
    queryString.split('&').forEach((param) => {
      const [key, value] = param.split('=');
      params[key] = value ? decodeURIComponent(value.replace(/\+/g, ' ')) : '';
    });
    return params;
  }

  /**
   * Get recipe id from current URL (?recipe=xxx).
   */
  getRecipeIdFromUrl() {
    try {
      const q = new URLSearchParams(window.location.search);
      const r = q.get('recipe');
      if (r) return r.trim();
    } catch (e) {}
    const match = window.location.href.match(/[?&]recipe=([^&]*)/);
    return match ? decodeURIComponent(match[1]).trim() : '';
  }

  /**
   * When user lands with ?recipe=xxx (from cart link), fetch cart, find line item keys for that recipe,
   * and set URL to ?editing=key1,key2 so the existing edit flow runs when they click Add to cart.
   */
  _maybeSetEditModeFromRecipeUrl() {
    const recipeId = this.getRecipeIdFromUrl();
    if (!recipeId) {
      if (window.location.search.includes('recipe')) {
        console.warn('[ProductCustomizer] recipe param present but getRecipeIdFromUrl returned empty');
      }
      return;
    }
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.has('editing')) return; // already in edit mode
    } catch (e) {
      return;
    }
    this.getLineItemKeysForRecipeId(recipeId).then((keys) => {
      if (!keys || keys.length === 0) {
        console.warn('[ProductCustomizer] edit mode: no cart line keys for recipe', recipeId, 'cart variant_id fallback uses', this.customProductVariantID);
        return;
      }
      const params = new URLSearchParams(window.location.search);
      params.set('editing', keys.join(','));
      const newUrl = window.location.pathname + '?' + params.toString() + (window.location.hash || '');
      window.history.replaceState(null, '', newUrl);
      this.updateCtaState('edit'); // Show "Update cart" when in edit mode from cart link
    });
  }

  /**
   * Retrieves the cart line-item keys being edited from the URL (?editing=key1,key2).
   * @returns {Array<String>} array of line item keys
   */
  getItemsBeingEdited() {
    const url = '?' + (window.location.search || '').replace(/^\?/, '');
    const query = this.parseQueryString(url);
    let raw = query.editing;
    if (raw == null || raw === '') return [];
    const arr = Array.isArray(raw) ? raw : [raw];
    return arr.flatMap((s) => String(s).split(',').map((k) => k.trim()).filter(Boolean));
  }

  /**
   * Fetches the cart and returns line-item keys for all items that belong to the
   * given recipe. Uses recipe_id or _recipe_id in properties; if none found, falls back
   * to line items with variant_id === this.customProductVariantID (parent custom product).
   * Sets this.cart for use by updateLineItemQuantitiesByKey.
   * @param {String} recipeId - Fluid Configure recipe id (e.g. from URL ?recipe=xxx)
   * @returns {Promise<Array<String>>} array of line item keys, or [] if none / fetch fails
   */
  getLineItemKeysForRecipeId(recipeId) {
    return fetch('/cart.js')
      .then((res) => res.json())
      .then((cart) => {
        this.cart = cart;
        if (!cart.items) return [];
        const idStr = String(recipeId || '');
        let keys = cart.items
          .filter((item) => {
            const p = item.properties || {};
            return String(p.recipe_id || p._recipe_id || '') === idStr;
          })
          .map((item) => item.key);
        if (keys.length === 0 && this.customProductVariantID) {
          keys = cart.items
            .filter((item) => String(item.variant_id) === String(this.customProductVariantID))
            .map((item) => item.key);
        }
        if (keys.length === 0 && cart.items.length) {
          console.warn('[ProductCustomizer] no keys matched recipe_id or variant_id; cart items:', cart.items.map((i) => ({ key: i.key, variant_id: i.variant_id, properties: i.properties })));
        }
        return keys;
      })
      .catch((err) => {
        console.warn('[ProductCustomizer] getLineItemKeysForRecipeId fetch failed', err);
        return [];
      });
  }

  /**
   * Provided an array of products, this method reduces out an array of all variants
   * @param {Array of Objects} products
   * @returns {Array of Objects}
   */
  _formatVariantsArray(products) {
    // Format an array of all variants.  Append product title as a prop
    const reducedProds = products.reduce(
      (acc, p) => (Array.isArray(p?.variants) ? [...acc, ...p.variants.map((v) => ({ ...v, name: p.title }))] : acc),
      []
    );

    console.log(reducedProds);
    return reducedProds;
  }
}

const prodSelectors = {
  productDetail: '[data-product-detail]',
  productCustomizer: '[data-product-customizer]',
  productRQ: '[data-prodcut-rq]',
};

new ProductCustomizer(prodSelectors.productCustomizer);
